import React, { useEffect, useState, useRef } from 'react';
import taskAPI from '../api/taskAPI';
import workerAPI from '../api/workerAPI';
import {
  FaEdit, FaTrash, FaUserPlus, FaTimes, FaChevronRight,
  FaLink, FaUpload, FaFilePdf, FaFileWord, FaFileImage,
  FaFile, FaDownload, FaEye, FaSpinner,
} from 'react-icons/fa';
import UserDashboard from './UserDashboard';

// ── Role badge ────────────────────────────────────────────────────────────────
const ROLE_COLOR = {
  'Marketing':     'bg-pink-100 text-pink-700',
  'Project Leads': 'bg-indigo-100 text-indigo-700',
  'HR & Design':   'bg-teal-100 text-teal-700',
  'TL':            'bg-blue-100 text-blue-700',
  'Developer':     'bg-green-100 text-green-700',
  'Office':        'bg-gray-100 text-gray-600',
};
const RoleBadge = ({ role }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLOR[role] || 'bg-gray-100 text-gray-600'}`}>
    {role}
  </span>
);

// ── Chain trail ───────────────────────────────────────────────────────────────
const ChainTrail = ({ chain }) => {
  if (!chain?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {chain.map((entry, i) => (
        <React.Fragment key={i}>
          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full">{entry}</span>
          {i < chain.length - 1 && <FaChevronRight className="text-purple-300 text-xs" />}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── File helpers ──────────────────────────────────────────────────────────────
const getFileType = (file) => {
  if (file.type.startsWith('image/'))  return 'image';
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) return 'doc';
  return 'other';
};
const FileIcon = ({ fileType }) => {
  if (fileType === 'image') return <FaFileImage className="text-blue-500 text-xl shrink-0" />;
  if (fileType === 'pdf')   return <FaFilePdf   className="text-red-500 text-xl shrink-0" />;
  if (fileType === 'doc')   return <FaFileWord  className="text-blue-700 text-xl shrink-0" />;
  return <FaFile className="text-gray-500 text-xl shrink-0" />;
};
const formatSize = (b) => b < 1024 ? `${b}B` : b < 1048576 ? `${(b/1024).toFixed(1)}KB` : `${(b/1048576).toFixed(1)}MB`;
const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

// ── FileUploadPanel (inline, no separate file needed) ─────────────────────────
const FileUploadPanel = ({ taskId, existingFiles = [], onUploaded }) => {
  const [pending, setPending]         = useState([]);
  const [uploading, setUploading]     = useState(false);
  const [uploaded, setUploaded]       = useState(existingFiles);
  const [previewUrl, setPreviewUrl]   = useState(null);
  const inputRef = useRef(null);

  const addFiles = (fileList) => {
    const mapped = Array.from(fileList).map(f => ({
      file: f, name: f.name, size: f.size,
      fileType: getFileType(f),
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    setPending(prev => [...prev, ...mapped]);
  };

  const handleUpload = async () => {
    if (!taskId)         { alert('Save the task first before uploading files.'); return; }
    if (!pending.length) { alert('No files selected.'); return; }
    setUploading(true);
    try {
      const files = await Promise.all(pending.map(async pf => ({
        base64: await toBase64(pf.file), name: pf.name, fileType: pf.fileType,
      })));
      const res = await taskAPI.post(`/${taskId}/attachments`, { files });
      setUploaded(res.data.attachments);
      setPending([]);
      onUploaded?.(res.data.attachments);
      alert(`✅ ${files.length} file(s) uploaded!`);
    } catch (err) {
      console.error(err);
      alert('❌ Upload failed.');
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await taskAPI.delete(`/${taskId}/attachments/${id}`);
      setUploaded(prev => prev.filter(f => f._id !== id));
    } catch { alert('❌ Delete failed.'); }
  };

  return (
    <div className="border border-purple-200 rounded-xl p-4 bg-purple-50 space-y-3">
      <h4 className="font-semibold text-purple-700 text-sm flex items-center gap-2">
        <FaUpload /> Attachments
        <span className="text-xs font-normal text-gray-500">(visible to all assignees)</span>
      </h4>

      {/* Drop zone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center cursor-pointer hover:bg-purple-100 transition"
      >
        <FaUpload className="mx-auto text-purple-400 text-xl mb-1" />
        <p className="text-sm text-gray-500">Click or drag & drop files here</p>
        <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, PDF, DOC, DOCX</p>
        <input ref={inputRef} type="file" multiple
          accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
          className="hidden" />
      </div>

      {/* Pending */}
      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ready to upload</p>
          {pending.map((pf, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              {pf.preview ? <img src={pf.preview} alt="" className="w-9 h-9 object-cover rounded" /> : <FileIcon fileType={pf.fileType} />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{pf.name}</p>
                <p className="text-xs text-gray-400">{formatSize(pf.size)}</p>
              </div>
              <button onClick={() => setPending(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><FaTrash /></button>
            </div>
          ))}
          <button onClick={handleUpload} disabled={uploading}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-2">
            {uploading ? <><FaSpinner className="animate-spin" /> Uploading...</> : <><FaUpload /> Upload {pending.length} file(s)</>}
          </button>
        </div>
      )}

      {/* Uploaded */}
      {uploaded.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Uploaded ({uploaded.length})</p>
          {uploaded.map(f => (
            <div key={f._id} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              <FileIcon fileType={f.fileType} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{f.name}</p>
                <p className="text-xs text-gray-400">by {f.uploadedBy} · {new Date(f.uploadedAt).toLocaleDateString('en-GB')}</p>
              </div>
              <div className="flex items-center gap-2">
                {f.fileType === 'image' && (
                  <button onClick={() => setPreviewUrl(f.url)} className="text-blue-500 hover:text-blue-700"><FaEye /></button>
                )}
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-700"><FaDownload /></a>
                <button onClick={() => handleDelete(f._id)} className="text-red-400 hover:text-red-600"><FaTrash /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!uploaded.length && !pending.length && (
        <p className="text-xs text-gray-400 text-center">No attachments yet.</p>
      )}

      {/* Image preview */}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-80 flex items-center justify-center px-4" onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setPreviewUrl(null)} className="absolute -top-10 right-0 text-white text-2xl">✕</button>
            <img src={previewUrl} alt="preview" className="w-full rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Constants ─────────────────────────────────────────────────────────────────
const CHAIN_ROLES = ['Office', 'HR & Design', 'Marketing', 'Project Leads', 'TL'];
const CAN_FORWARD = ['Office', 'HR & Design', 'Marketing', 'Project Leads', 'TL'];

// ─────────────────────────────────────────────────────────────────────────────
const OfficeTaskManager = () => {
  const worker     = JSON.parse(localStorage.getItem('worker'));
  const workerRole = worker?.role;
  const workerId   = worker?._id;
  const isAdminPanel = !worker;

  const [tasks, setTasks]           = useState([]);
  const [allWorkers, setAllWorkers] = useState([]);
  const [assignable, setAssignable] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const tableRef = useRef(null);

  const [savedTaskId, setSavedTaskId]       = useState(null);
  const [savedTaskTitle, setSavedTaskTitle] = useState('');

  const [legacyForm, setLegacyForm] = useState({
    title: '', assigneeId: '',
    today: new Date().toISOString().split('T')[0],
    deadline: '', status: 'Pending', description: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId]       = useState(null);

  const [chainForm, setChainForm]                 = useState({ title: '', deadline: '', description: '' });
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [assigneeDropOpen, setAssigneeDropOpen]   = useState(false);

  const [showForwardModal, setShowForwardModal]     = useState(false);
  const [forwardTask, setForwardTask]               = useState(null);
  const [forwardWorkers, setForwardWorkers]         = useState([]);
  const [forwardSelectedIds, setForwardSelectedIds] = useState([]);

  const [showChainModal, setShowChainModal] = useState(false);
  const [chainTask, setChainTask]           = useState(null);

  const [showAttachModal, setShowAttachModal] = useState(false);
  const [attachTask, setAttachTask]           = useState(null);

  useEffect(() => {
    fetchTasks();
    if (isAdminPanel) fetchAllWorkers();
    else if (CHAIN_ROLES.includes(workerRole)) fetchAssignable();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await taskAPI.get(isAdminPanel ? '/' : '/created-by-me');
      setTasks(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchAllWorkers = async () => {
    try { const res = await workerAPI.get('/'); setAllWorkers(res.data); }
    catch (err) { console.error(err); }
  };

  const fetchAssignable = async () => {
    try { const res = await taskAPI.get('/assignable-workers'); setAssignable(res.data); }
    catch (err) { console.error(err); }
  };

  const handleLegacySubmit = async () => {
    if (!legacyForm.title || !legacyForm.assigneeId || !legacyForm.deadline || !legacyForm.description) {
      alert('Please fill all required fields!'); return;
    }
    try {
      if (isEditing) {
        await taskAPI.put(`/${editId}`, legacyForm);
        setSavedTaskId(editId); setSavedTaskTitle(legacyForm.title);
        alert('Task updated! You can now manage attachments below.');
      } else {
        const res = await taskAPI.post('', legacyForm);
        setSavedTaskId(res.data._id); setSavedTaskTitle(res.data.title);
        alert('Task added! You can now upload attachments below.');
      }
      setLegacyForm({ title: '', assigneeId: '', today: new Date().toISOString().split('T')[0], deadline: '', status: 'Pending', description: '' });
      setIsEditing(false); setEditId(null);
      fetchTasks();
      setTimeout(() => tableRef.current?.scrollIntoView({ behavior: 'smooth' }), 300);
    } catch (err) { console.error(err); }
  };

  const handleEditTask = (task) => {
    setLegacyForm(task); setIsEditing(true); setEditId(task._id);
    setSavedTaskId(task._id); setSavedTaskTitle(task.title);
  };

  const handleDeleteTask = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await taskAPI.delete(`/${id}`);
      if (savedTaskId === id) { setSavedTaskId(null); setSavedTaskTitle(''); }
      fetchTasks();
    } catch (err) { console.error(err); }
  };

  const handleChainSubmit = async (e) => {
    e.preventDefault();
    if (!chainForm.title || !chainForm.deadline || !chainForm.description) { alert('Fill all fields.'); return; }
    if (!selectedAssignees.length) { alert('Select at least one assignee.'); return; }
    try {
      const res = await taskAPI.post('/chain', { ...chainForm, assigneeIds: selectedAssignees });
      setSavedTaskId(res.data._id); setSavedTaskTitle(res.data.title);
      alert('✅ Task assigned! You can now upload attachments below.');
      setChainForm({ title: '', deadline: '', description: '' });
      setSelectedAssignees([]); setAssigneeDropOpen(false);
      fetchTasks();
    } catch (err) { alert(err.response?.data?.error || '❌ Failed.'); }
  };

  const toggleAssignee = (id) =>
    setSelectedAssignees(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const openForwardModal = async (task) => {
    setForwardTask(task); setForwardSelectedIds([]);
    try {
      const res = await taskAPI.get('/assignable-workers');
      const existingIds = (task.assignees || []).map(a => a.userId?.toString());
      setForwardWorkers(res.data.filter(w => !existingIds.includes(w._id)));
    } catch (err) { console.error(err); }
    setShowForwardModal(true);
  };

  const handleForward = async () => {
    if (!forwardSelectedIds.length) { alert('Select at least one worker.'); return; }
    try {
      await taskAPI.post(`/${forwardTask._id}/forward`, { assigneeIds: forwardSelectedIds });
      alert('✅ Task forwarded!'); setShowForwardModal(false); fetchTasks();
    } catch (err) { alert(err.response?.data?.error || '❌ Forward failed.'); }
  };

  const getWorkerName = (id) => allWorkers.find(w => w._id === id)?.username || 'Unknown';

  const filtered = tasks.filter(t => {
    const matchSearch =
      t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getWorkerName(t.assigneeId).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.assignees || []).some(a => a.username?.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchSearch && (statusFilter === 'All' || t.status === statusFilter);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-6 pt-20">
      <UserDashboard />

      <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center mb-5 mt-3 gap-3">
        <div>
          <h2 className="text-3xl font-bold font-mono text-purple-700">Task Management</h2>
          {worker && <p className="text-sm text-gray-500 mt-0.5">{worker.username} <RoleBadge role={workerRole} /></p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {['All','Pending','In Progress','Completed'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${statusFilter === s ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* ── LEFT: Form + File Upload ───────────────────────────────────────── */}
        <div className="bg-white p-6 rounded-xl shadow-lg overflow-y-auto max-h-[85vh]">

          {isAdminPanel && (
            <>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">{isEditing ? 'Edit Task' : 'Assign New Task'}</h3>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Title</label>
              <input type="text" value={legacyForm.title}
                onChange={e => setLegacyForm({ ...legacyForm, title: e.target.value })}
                className="border rounded-lg px-4 py-2 w-full mb-4" />
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
              <select value={legacyForm.assigneeId}
                onChange={e => setLegacyForm({ ...legacyForm, assigneeId: e.target.value })}
                className="border rounded-lg px-4 py-2 w-full mb-3">
                <option value="" disabled hidden>Select a worker</option>
                {allWorkers.filter(w => ['HR & Design','Marketing','Project Leads','TL','Developer'].includes(w.role))
                  .map(w => <option key={w._id} value={w._id}>{w.username} ({w.role})</option>)}
              </select>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Today's Date</label>
                  <input type="date" value={legacyForm.today} readOnly className="border rounded-lg px-4 py-2 w-full bg-gray-100" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Deadline</label>
                  <input type="date" value={legacyForm.deadline}
                    onChange={e => setLegacyForm({ ...legacyForm, deadline: e.target.value })}
                    className="border rounded-lg px-4 py-2 w-full" />
                </div>
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={legacyForm.status} onChange={e => setLegacyForm({ ...legacyForm, status: e.target.value })}
                className="border rounded-lg px-4 py-2 w-full mb-3">
                <option>Pending</option><option>In Progress</option><option>Completed</option>
              </select>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Description</label>
              <textarea value={legacyForm.description}
                onChange={e => setLegacyForm({ ...legacyForm, description: e.target.value })}
                className="border rounded-lg px-4 py-2 w-full mb-3" rows="2" />
              <button onClick={handleLegacySubmit}
                className={`${isEditing ? 'bg-blue-500 hover:bg-blue-600' : 'bg-purple-500 hover:bg-purple-600'} text-white px-6 py-2 rounded-lg w-full`}>
                {isEditing ? 'Update Task' : 'Add Task'}
              </button>
              {savedTaskId && (
                <div className="mt-3">
                  <p className="text-xs text-green-600 font-medium mb-2">
                    ✅ Files for: <span className="font-bold">{savedTaskTitle}</span>
                  </p>
                  <FileUploadPanel taskId={savedTaskId}
                    existingFiles={tasks.find(t => t._id === savedTaskId)?.attachments || []}
                    onUploaded={(attachments) => setTasks(prev => prev.map(t => t._id === savedTaskId ? { ...t, attachments } : t))} />
                </div>
              )}
            </>
          )}

          {!isAdminPanel && CHAIN_ROLES.includes(workerRole) && (
            <>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Assign New Task</h3>
              <form onSubmit={handleChainSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Task Title</label>
                  <input type="text" value={chainForm.title}
                    onChange={e => setChainForm({ ...chainForm, title: e.target.value })} required
                    className="border rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-purple-400 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea rows={2} value={chainForm.description}
                    onChange={e => setChainForm({ ...chainForm, description: e.target.value })} required
                    className="border rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-purple-400 outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Today</label>
                    <input type="date" value={new Date().toISOString().split('T')[0]} readOnly className="border rounded-lg px-4 py-2 w-full bg-gray-100" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deadline</label>
                    <input type="date" value={chainForm.deadline}
                      onChange={e => setChainForm({ ...chainForm, deadline: e.target.value })} required
                      className="border rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-purple-400 outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                  <button type="button" onClick={() => setAssigneeDropOpen(!assigneeDropOpen)}
                    className="border px-4 py-2 rounded-lg w-full text-left bg-white text-sm">
                    {selectedAssignees.length === 0 ? 'Select workers...' : `${selectedAssignees.length} worker(s) selected`}
                  </button>
                  {assigneeDropOpen && (
                    <div className="mt-1 border rounded-lg bg-white shadow z-20 max-h-44 overflow-y-auto">
                      {assignable.length === 0
                        ? <p className="text-sm text-gray-400 p-3 text-center">No assignable workers found.</p>
                        : assignable.map(w => (
                          <label key={w._id} className={`flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-purple-50 text-sm ${selectedAssignees.includes(w._id) ? 'bg-purple-50' : ''}`}>
                            <input type="checkbox" checked={selectedAssignees.includes(w._id)}
                              onChange={() => toggleAssignee(w._id)} className="accent-purple-600" />
                            <span className="flex-1">{w.username}</span>
                            <RoleBadge role={w.role} />
                            {w.team && <span className="text-xs text-gray-400">{w.team}</span>}
                          </label>
                        ))}
                    </div>
                  )}
                  {selectedAssignees.length > 0 && <p className="text-xs text-purple-600 mt-1">{selectedAssignees.length} selected</p>}
                </div>
                <button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg w-full font-semibold">
                  Assign Task
                </button>
              </form>
              {savedTaskId && (
                <div className="mt-3">
                  <p className="text-xs text-green-600 font-medium mb-2">
                    ✅ Files for: <span className="font-bold">{savedTaskTitle}</span>
                  </p>
                  <FileUploadPanel taskId={savedTaskId}
                    existingFiles={tasks.find(t => t._id === savedTaskId)?.attachments || []}
                    onUploaded={(attachments) => setTasks(prev => prev.map(t => t._id === savedTaskId ? { ...t, attachments } : t))} />
                </div>
              )}
            </>
          )}
        </div>

        {/* ── RIGHT: Task Table ──────────────────────────────────────────────── */}
        <div ref={tableRef} className="bg-white p-4 rounded-xl shadow-lg overflow-x-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-3">
            <h3 className="text-xl font-semibold text-purple-600">{isAdminPanel ? 'All Tasks' : 'Tasks I Created'}</h3>
            <input type="text" placeholder="Search by name or title..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="border px-4 py-2 rounded-lg w-full md:w-72 text-sm" />
          </div>

          <div className="overflow-y-auto max-h-[450px]">
            <table className="min-w-full table-auto text-sm">
              <thead className="bg-purple-100 text-purple-700 sticky top-0 z-10">
                <tr>
                  <th className="py-3 px-3 text-left">Date</th>
                  <th className="py-3 px-3 text-left">Assigned To</th>
                  <th className="py-3 px-3 text-left">Title</th>
                  <th className="py-3 px-3 text-left">Status</th>
                  <th className="py-3 px-3 text-left">Files</th>
                  <th className="py-3 px-3 text-left">Chain</th>
                  <th className="py-3 px-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {filtered.map(task => (
                  <tr key={task._id} className="border-t hover:bg-purple-50 transition">
                    <td className="py-2 px-3 whitespace-nowrap text-xs">
                      {task.today ? new Date(task.today).toLocaleDateString('en-GB') : new Date(task.createdAt).toLocaleDateString('en-GB')}
                    </td>
                    <td className="py-2 px-3">
                      {task.assignees?.length > 0
                        ? <div className="flex flex-wrap gap-1">
                            {task.assignees.map((a,i) => (
                              <span key={i} className="flex items-center gap-1 text-xs bg-gray-50 border rounded-full px-2 py-0.5">
                                {a.username} <RoleBadge role={a.role} />
                              </span>
                            ))}
                          </div>
                        : <span className="text-xs">{getWorkerName(task.assigneeId)}</span>}
                    </td>
                    <td className="py-2 px-3 font-medium">{task.title}</td>
                    <td className={`py-2 px-3 font-semibold text-xs ${task.status === 'Completed' ? 'text-green-600' : task.status === 'In Progress' ? 'text-blue-600' : 'text-yellow-600'}`}>
                      {task.status}
                    </td>
                    {/* Files */}
                    <td className="py-2 px-3">
                      <button onClick={() => { setAttachTask(task); setShowAttachModal(true); }}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
                        <FaUpload />
                        {task.attachments?.length > 0 ? `${task.attachments.length} file(s)` : 'Add files'}
                      </button>
                    </td>
                    {/* Chain */}
                    <td className="py-2 px-3">
                      {task.assignmentChain?.length > 0
                        ? <button onClick={() => { setChainTask(task); setShowChainModal(true); }}
                            className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium">
                            <FaLink /> View
                          </button>
                        : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    {/* Actions */}
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isAdminPanel && (
                          <>
                            <FaEdit className="text-blue-500 cursor-pointer hover:text-blue-700" onClick={() => handleEditTask(task)} />
                            <FaTrash className="text-red-500 cursor-pointer hover:text-red-700" onClick={() => handleDeleteTask(task._id)} />
                          </>
                        )}
                        {!isAdminPanel && CAN_FORWARD.includes(workerRole) && task.status !== 'Completed' && (
                          <button onClick={() => openForwardModal(task)}
                            className="flex items-center gap-1 text-xs bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded-lg">
                            <FaUserPlus /> Forward
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && <tr><td colSpan="7" className="text-center py-8 text-gray-400">No tasks found.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── ATTACHMENT MODAL (from table) ─────────────────────────────────────── */}
      {showAttachModal && attachTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowAttachModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Files</h3>
            <p className="text-sm text-gray-500 mb-4">"<span className="font-medium text-gray-700">{attachTask.title}</span>"</p>
            <FileUploadPanel taskId={attachTask._id} existingFiles={attachTask.attachments || []}
              onUploaded={(attachments) => {
                setTasks(prev => prev.map(t => t._id === attachTask._id ? { ...t, attachments } : t));
                setAttachTask(prev => ({ ...prev, attachments }));
              }} />
          </div>
        </div>
      )}

      {/* ── FORWARD MODAL ────────────────────────────────────────────────────── */}
      {showForwardModal && forwardTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={() => setShowForwardModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Forward Task</h3>
            <p className="text-sm text-gray-500 mb-3">"<span className="font-medium text-gray-700">{forwardTask.title}</span>"</p>
            {forwardTask.assignmentChain?.length > 0 && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Current chain:</p>
                <ChainTrail chain={forwardTask.assignmentChain} />
                <div className="flex items-center gap-1 mt-1">
                  <FaChevronRight className="text-purple-300 text-xs" />
                  <span className="text-xs bg-purple-100 text-purple-700 border border-purple-300 px-2 py-0.5 rounded-full font-semibold">
                    {worker?.username} ({workerRole}) → ?
                  </span>
                </div>
              </div>
            )}
            <div className="border rounded-lg divide-y max-h-56 overflow-y-auto mb-4">
              {forwardWorkers.length === 0
                ? <p className="text-sm text-gray-400 p-4 text-center">No eligible workers.</p>
                : forwardWorkers.map(w => (
                  <label key={w._id} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-purple-50 ${forwardSelectedIds.includes(w._id) ? 'bg-purple-50' : ''}`}>
                    <input type="checkbox" checked={forwardSelectedIds.includes(w._id)}
                      onChange={() => setForwardSelectedIds(prev => prev.includes(w._id) ? prev.filter(x => x !== w._id) : [...prev, w._id])}
                      className="accent-purple-600" />
                    <span className="flex-1 text-sm font-medium">{w.username}</span>
                    <RoleBadge role={w.role} />
                  </label>
                ))}
            </div>
            {forwardSelectedIds.length > 0 && <p className="text-xs text-purple-600 mb-3">{forwardSelectedIds.length} selected</p>}
            <button onClick={handleForward} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 rounded-lg">
              Forward Task
            </button>
          </div>
        </div>
      )}

      {/* ── CHAIN MODAL ──────────────────────────────────────────────────────── */}
      {showChainModal && chainTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={() => setShowChainModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Assignment Chain</h3>
            <p className="text-sm text-gray-500 mb-4">"<span className="font-medium text-gray-700">{chainTask.title}</span>"</p>
            <div className="mb-4">
              <p className="text-xs text-gray-400 mb-2">Delegation path:</p>
              <ChainTrail chain={chainTask.assignmentChain} />
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-2">All assignees:</p>
              <div className="space-y-2">
                {chainTask.assignees?.map((a, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${a.userId?.toString() === workerId ? 'bg-purple-50 border-purple-300' : 'bg-gray-50 border-gray-200'}`}>
                    <span className="text-sm font-medium flex-1">{a.username}</span>
                    <RoleBadge role={a.role} />
                    <span className="text-xs text-gray-400">← {a.assignedBy}</span>
                    {a.userId?.toString() === workerId && <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">You</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfficeTaskManager;